import {
  Flex,
  Box,
  FormControl,
  AbsoluteCenter,
  InputGroup,
  Input,
  Stack,
  Button,
  FormErrorMessage,
  Text,
  useColorModeValue,
  Link,
  Image,
  Divider,
  InputLeftElement,
} from '@chakra-ui/react';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLoginMutation } from "../../slices/userApiSlice";
import { setCredentials } from "../../slices/authSlice";
import { PasswordField } from '../../components/PasswordField';
import { ToastService } from '../../services/toast/toastService';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAt } from "@fortawesome/free-solid-svg-icons"; 
import '../../components/GoogleButton.css';

const LoginScreen = () => {
const [email, setEmail] = useState("");
const [password, setPassword] = useState("");
const navigate = useNavigate();
const dispatch = useDispatch();
const { error } = ToastService();
const [login, { isLoading }] = useLoginMutation();
const { userInfo } = useSelector((state) => state.auth);
const [formErrors, setFormErrors] = useState();
const bgColor = useColorModeValue('gray.50', 'gray.700');
const textColor = useColorModeValue('custom.800', 'custom.900');
const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

useEffect(() => {
  if (userInfo) {
    navigate("/");
  }

  // Load reCAPTCHA v3 script
  const loadRecaptcha = () => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setRecaptchaLoaded(true);
    };
    document.body.appendChild(script);
  };

  if (!window.grecaptcha) {
    loadRecaptcha();
  } else {
    setRecaptchaLoaded(true);
  }

  return () => {
    const scripts = document.querySelectorAll('script[src^="https://www.google.com/recaptcha/api.js"]');
    scripts.forEach(script => document.body.removeChild(script));
  };
}, [navigate, userInfo]);

const validateForm = () => {
  let errors = {};
  if (!email) {
    errors.email = "Please enter your email address.";
  }
  if (!password) {
    errors.password = "Please enter your password.";
  }
  return errors;
};

const handleEmailChange = (e) => {
  const value = e.target.value;
  setEmail(value);
  setFormErrors((prevErrors) => ({
    ...prevErrors,
    email: "",
  }));
};

const handlePasswordChange = (e) => {
  const value = e.target.value;
  setPassword(value);
  setFormErrors((prevErrors) => ({
    ...prevErrors,
    password: "",
  }));
};

const submitHandler = async (e) => {
  e.preventDefault();

  // Validate form
  const validationErrors = validateForm();
  if (Object.keys(validationErrors).length > 0) {
    setFormErrors(prevErrors => ({
      ...prevErrors,
      ...validationErrors
    }));
    return;
  }

  try {
    if (!recaptchaLoaded) {
      error("Security verification is still loading. Please try again.");
      return;
    }

    // Execute reCAPTCHA v3
    const token = await window.grecaptcha.execute(
      process.env.REACT_APP_RECAPTCHA_SITE_KEY, 
      { action: 'login' }
    );

    const response = await login({ 
      email, 
      password, 
      recaptchaToken: token 
    }).unwrap();
    
    if (response.success) {
      dispatch(setCredentials({ ...response.user }));
    } else {
      error(response.message || 'Invalid login response.');
    }
  } catch (err) {
    error(err.data?.message || 'An unexpected error occurred.');
  }
};

return (    
  <Flex
    minH={'100vh'}
    justify={'center'}      
    bg={bgColor}
    color={textColor}
    width="100%"
    pt={`calc(1rem + env(safe-area-inset-top))`}
    >
    <Stack
        w={'full'}
        maxW={'lg'}
        p={8}
        >
        <Image 
          src="/logo512.png" 
          alt="iFun Baby Logo" 
          boxSize="150px" 
          mx="auto" 
          mt={4}
        />
        <Text display='flex' justifyContent='center' alignItems='center' fontSize={{ base: 'md', sm: 'lg' }} mb="4">
            Please login via email.
        </Text>
        
        {/* <form onSubmit={submitHandler}> */}
          <Stack spacing={4}>
            <FormControl isRequired isInvalid={!!formErrors?.email}>
              <InputGroup>
                <InputLeftElement>
                  <FontAwesomeIcon
                    color='gray'
                    icon={faAt}
                  />
                </InputLeftElement>
                <Input 
                  id="email"
                  type="email" 
                  value={email}
                  placeholder='Email address'
                  autoFocus
                  autoComplete='false'
                  onChange={handleEmailChange} />
              </InputGroup>
              <FormErrorMessage>{formErrors?.email}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!formErrors?.password}>
              <InputGroup>
                <PasswordField 
                  width={'100%'}
                  id="password"
                  value={password}
                  placeholder='Password'
                  onChange={handlePasswordChange}
                  showStrengthBar={false}
                  />
              </InputGroup>
              <FormErrorMessage>{formErrors?.password}</FormErrorMessage>
            </FormControl>
          
            <Stack spacing={4}>
              <Button
                // type="submit"
                colorScheme='teal'
                onClick={submitHandler} 
                isLoading={isLoading}
                //loadingText="Logging in..."
                >
                Log in
              </Button>
              <Stack
                direction="row"
                width="100%"
                alignItems="flex-start"
                justifyContent="space-between"
                flexWrap="nowrap"
                color="#1A73E8"
                >
                  <Link href='/forget-password'>
                    Forgot password?
                  </Link>
                  <Link href='/register'>
                      Sign up
                  </Link>
              </Stack>
            </Stack>

            <Box position='relative' color='gray.300' padding={'10px'} >
              <Divider borderColor="gray.300" />
              <AbsoluteCenter bg={bgColor} px='5'>
                <Text fontSize={'md'} fontWeight={'semibold'}>OR</Text>
              </AbsoluteCenter>
            </Box>

            <Stack align="center" justifyContent={"center"}>
              <a href={`${process.env.REACT_APP_API_BASE_URL}/auth/google`} className="gsi-material-button">
                <div className="gsi-material-button-state"></div>
                <div className="gsi-material-button-content-wrapper">
                  <div className="gsi-material-button-icon">
                    <svg
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 48 48"
                      style={{ display: 'block' }}
                    >
                      <path
                        fill="#EA4335"
                        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                      ></path>
                      <path
                        fill="#4285F4"
                        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                      ></path>
                      <path
                        fill="#FBBC05"
                        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                      ></path>
                      <path
                        fill="#34A853"
                        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                      ></path>
                    </svg>
                  </div>
                  <div>
                    <span className="gsi-material-button-contents">
                      Log in with Google
                    </span>
                  </div>
                </div>
              </a>
            </Stack>
          </Stack>
        {/* </form> */}
      </Stack>
    </Flex>
);
}
  
export default LoginScreen;

// import {
//     Flex,
//     Box,
//     FormControl,
//     AbsoluteCenter,
//     InputGroup,
//     Input,
//     Stack,
//     Button,
//     FormErrorMessage,
//     Text,
//     useColorModeValue,
//     Link,
//     Image,
//     Divider,
//     InputLeftElement,
// } from '@chakra-ui/react';
// import { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { useDispatch, useSelector } from "react-redux";
// import { useLoginMutation } from "../../slices/userApiSlice";
// import { setCredentials } from "../../slices/authSlice";
// import { PasswordField } from '../../components/PasswordField';
// import { ToastService } from '../../services/toast/toastService';
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faAt } from "@fortawesome/free-solid-svg-icons"; 
// import '../../components/GoogleButton.css';
// <script src="https://www.google.com/recaptcha/api.js" async defer></script>

// const LoginScreen = () => {
//   const [email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const { error } = ToastService();
//   const [login, { isLoading }] = useLoginMutation();
//   const { userInfo } = useSelector((state) => state.auth);
//   const [formErrors, setFormErrors] = useState();
//   const bgColor = useColorModeValue('gray.50', 'gray.700');
//   const textColor = useColorModeValue('custom.800', 'custom.900');

//   const [recaptchaToken, setRecaptchaToken] = useState(null);
  
//   const handleRecaptchaChange = (token) => {
//     setRecaptchaToken(token);
//   };

//   useEffect(() => {
//     if (userInfo) {
//       navigate("/");
//     }
//   }, [navigate, userInfo]);

//   const validateForm = () => {
//     let errors = {};
//     if (!email) {
//       errors.email = "Please enter your email address.";
//     }
//     if (!password) {
//       errors.password = "Please enter your password.";
//     }
//     return errors;
//   };

//   const handleEmailChange = (e) => {
//     const value = e.target.value;
  
//     // Update name state
//     setEmail(value);
  
//     // Clear error message if input value changes
//     setFormErrors((prevErrors) => ({
//       ...prevErrors,
//       email: "", // Clear the error for the name field
//     }));
//   };

//   const handlePasswordChange = (e) => {
//     const value = e.target.value;
  
//     // Update name state
//     setPassword(value);
  
//     // Clear error message if input value changes
//     setFormErrors((prevErrors) => ({
//       ...prevErrors,
//       password: "", // Clear the error for the name field
//     }));
//   };

//   const submitHandler = async (e) => {
//     e.preventDefault();

//     if (!recaptchaToken) {
//       alert("Please complete the reCAPTCHA verification");
//       return;
//     }
//     // Validate form
//     const validationErrors = validateForm();
//     if (Object.keys(validationErrors).length > 0) {
//         setFormErrors(prevErrors => ({
//         ...prevErrors,
//         ...validationErrors
//         }));
//         return;
//     }
    
//     try {
//       //const isAdmin = false; // You can make this dynamic if needed
//       const response = await login({ email, password, recaptchaToken }).unwrap();
      
//       if (response.success) {
//         // Set user credentials in Redux store
//         dispatch(setCredentials({ ...response.user }));
        
//       } else {
//         error(response.message || 'Invalid login response.');
//       }
//     } catch (err) {
//       error(err.data?.message || 'An unexpected error occurred.')
//     }
//   };
 
//   return (    
//     <Flex
//       minH={'100vh'}
//       justify={'center'}      
//       bg={bgColor}
//       color={textColor}
//       width="100%">
//       <Stack
//           w={'full'}
//           maxW={'lg'}
//           p={8}
//           >
//           <Image 
//             src="/logo512.png" 
//             alt="iFun Baby Logo" 
//             boxSize="150px" 
//             mx="auto" 
//             mt={4}
//           />
//           <Text display='flex' justifyContent='center' alignItems='center' fontSize={{ base: 'md', sm: 'lg' }} mb="4">
//               Please login via email.
//           </Text>
          
//             <form onSubmit={submitHandler}>
//             <Stack spacing={4}>

//             {/* reCAPTCHA Widget */}
//             <div 
//               className="g-recaptcha" 
//               data-sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
//               data-callback={handleRecaptchaChange}
//               data-expired-callback={() => setRecaptchaToken(null)}
//             ></div>

//               <FormControl isRequired isInvalid={!!formErrors?.email}>
//                 <InputGroup>
//                   <InputLeftElement>
//                     <FontAwesomeIcon
//                       color='gray'
//                       icon={faAt}
//                     />
//                   </InputLeftElement>
//                   <Input 
//                     id="email"
//                     type="email" 
//                     value={email}
//                     placeholder='Email address'
//                     autoFocus
//                     autoComplete='false'
//                     //bg={bgColor}
//                     onChange={handleEmailChange} />
//                 </InputGroup>
//                 <FormErrorMessage>{formErrors?.email}</FormErrorMessage>
//               </FormControl>

//               <FormControl isRequired isInvalid={!!formErrors?.password}>
//                 <InputGroup>
//                   <PasswordField 
//                     width={'100%'}
//                     id="password"
//                     value={password}
//                     placeholder='Password'
//                     //bg={bgColor}
//                     onChange={handlePasswordChange}
//                     showStrengthBar={false}
//                     />
//                 </InputGroup>
//                 <FormErrorMessage>{formErrors?.password}</FormErrorMessage>
//               </FormControl>
            
//               <Stack spacing={4}>
//                 <Button
//                   type="submit"
//                   colorScheme='teal'
//                   //onClick={submitHandler}
//                   isLoading={isLoading}
//                   //isDisabled={!recaptchaToken}
//                   >
//                   Log in
//                 </Button>
//                 <Stack
//                   direction="row"
//                   width="100%"
//                   alignItems="flex-start"
//                   justifyContent="space-between"
//                   flexWrap="nowrap"
//                   color="#1A73E8"
//                   >
//                     <Link href='/forget-password'>
//                       Forgot password?
//                     </Link>

//                     <Link href='/register'>
//                         Sign up
//                     </Link>
//                 </Stack>
//               </Stack>

//               <Box position='relative' color='gray.300' padding={'10px'} >
//                 <Divider borderColor="gray.300" />
//                 <AbsoluteCenter bg={bgColor} px='5'>
//                   <Text fontSize={'md'} fontWeight={'semibold'}>OR</Text>
//                 </AbsoluteCenter>
//               </Box>

//               {/* Google Login Button */}
//                 <Stack 
//                   align="center" 
//                   justifyContent={"center"}
//                   >
                
//                   <a href={`${process.env.REACT_APP_API_BASE_URL}/auth/google`} className="gsi-material-button">
//                     <div className="gsi-material-button-state"></div>
//                     <div className="gsi-material-button-content-wrapper">
//                       <div className="gsi-material-button-icon">
//                         <svg
//                           version="1.1"
//                           xmlns="http://www.w3.org/2000/svg"
//                           viewBox="0 0 48 48"
//                           style={{ display: 'block' }}
//                         >
//                           <path
//                             fill="#EA4335"
//                             d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
//                           ></path>
//                           <path
//                             fill="#4285F4"
//                             d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
//                           ></path>
//                           <path
//                             fill="#FBBC05"
//                             d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
//                           ></path>
//                           <path
//                             fill="#34A853"
//                             d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
//                           ></path>
//                         </svg>
//                       </div>

//                       <div>
//                         <span className="gsi-material-button-contents">
//                           Log in with Google
//                         </span>
//                       </div>
//                     </div>
//                   </a>
//                   {/* )} */}
//                 </Stack>
//             </Stack>
//           </form>
//         </Stack>
//       </Flex>
//   )
// }
    
// export default LoginScreen;
